import {DataTransformGraph} from "./data-transform-graph/index";
import {getElement, htmlEntities, insertHTMLBefore, isStandalone} from "./utils";
import {assign, constant, arrayRemove} from "./voidash.js";
import Router from "./Router";
import Sentinel from "./Sentinel";
import Template from "./Template";
import ExtendableObject from "./ExtendableObject";
import {EventEmitter, Event/*,DestroyEvent*/} from "./Events";
import View from "./View";
import TreeStore from "./TreeStore";

//=============================================================================
//=============================================================================
// BasicView
//=============================================================================
//=============================================================================

const BasicView = View.extend({
  init: function () {
    this.textNode = null;
  },

  getName: function () {
    return "basic_view";
  },

  getHTML: function () {
    return this.beginSentinel.getHTML() + (this.model || "") + this.endSentinel.getHTML();
  },

  handleMounted: function () {
    this.handleAlts();
    this.findTextNode();
  },

  handleUnmounted: function () {
    this.textNode = null;
  },

  setHTML: function (html) {
    this.model = html;
    if (!this.isMounted) return;
    this.clear();
    insertHTMLBefore(this.model, this.endSentinel.getReference());

    this.handleAlts();
    this.findTextNode();
  },

  clear: function () {
    let node = this.beginSentinel.getReference();
    if (!node) return;
    const end = this.endSentinel.getReference();
    node = node.nextSibling; // do not remove the begin sentinel
    const parentNode = node.parentNode;
    while (node !== end) {
      const next = node.nextSibling;
      parentNode.removeChild(node);
      node = next;
    }
  },

  setText: function (text) {
    this.model = htmlEntities(text);

    if (!this.isMounted) return;

    if (this.textNode) {
      this.textNode.textContent = text;
    } else {
      this.clear();
      this.textNode = document.createTextNode(text);
      var ref = this.endSentinel.getReference();
      ref.parentNode.insertBefore(this.textNode, ref);
    }

    this.handleAlts();
    this.findTextNode();
  },

  findTextNode() {
    this.textNode = null;
    var node = this.beginSentinel.getReference();
    if (!node) return;
    node = node.nextSibling;
    if (node.nodeType === Node.TEXT_NODE && node.nextSibling === this.endSentinel.getReference()) {
      this.textNode = node;
    }
  }
});

//=============================================================================
//=============================================================================
// DataView
//=============================================================================
//=============================================================================

const DataView = View.extend({
  init() {
    if (!this.opts.templateName) throw new Error("DataView: opts.templateName not set");

    this.observerNodes = [];

    Object.keys(this.model).forEach(k => {
      const view = new BasicView("", this.opts);
      this.registerSubview(view, k);
      this.observerNodes.push(this.model[k].o(v => view.setText(this.formatValue(v))));
    });

    if (this.opts.addFields) {
      Object.keys(this.opts.addFields).forEach(k => {
        const {keys, format} = this.opts.addFields[k];
        const view = new BasicView("", this.opts);
        this.registerSubview(view, k);
        this.observerNodes.push(this.model.oa(keys, a => view.setText(format(...a))));
      });
    }
  },

  getTemplateName() {
    return this.opts.templateName;
  },

  formatValue(val) {
    if (this.opts.globalFormat) return this.opts.globalFormat(val);
    return val;
  },

  handleDestroyed() {
    this.observerNodes.forEach(n => n.deactivate());
  }
});

//=============================================================================
//=============================================================================
// export
//=============================================================================
//=============================================================================

export default {
  Sentinel,
  Router,
  ExtendableObject,
  Event,
  EventEmitter,
  View,
  Template,
  BasicView,
  DataTransformGraph,
  TreeStore,
  DataView,
  isStandalone,
  utils: {
    assign,
    constant,
    getElement,
    removeFromArray: arrayRemove,
    insertHTMLBefore
  }
};
